import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import DataTable from "react-data-table-component";
import sh from "bundles/common/utils/sh";
import { Link, Box, Chip, Container, CircularProgress, TextField, InputAdornment, Tooltip } from "@mui/material";
import { FormControl, InputLabel, Divider, Select, Checkbox, Button, ListItemText, ListSubheader, Pagination, OutlinedInput, MenuItem } from "@mui/material";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import SearchIcon from "@mui/icons-material/Search";
import debounce from "lodash/debounce";

import { belgianRegions, frenchRegions, jobStatuses, statusChipColor } from "bundles/common/utils/constants";
import { capitalize, formatRegion, organizeCategoriesByDepartment } from "bundles/common/utils/helpers";


const Resumes = () => {
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedJobStatuses, setSelectedJobStatuses] = useState([]);
  const [query, setQuery] = useState("");

  const [organizedCategories, setOrganizedCategories] = useState({});

  const [currentPage, setCurrentPage] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const [count, setCount] = useState(0);

  const customStyles = {
    headCells: {
      style: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "&:nth-last-of-type(2)": {
          borderLeft: "0.4px solid #DDDDDD",
        },
      }
    },
    cells: {
      style: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "&:nth-last-of-type(2)": {
          borderLeft: "0.4px solid #DDDDDD",
        },
      }
    },
    responsiveWrapper: {
      style: {
        overflowX: "auto"
      }
    },
    table: {
      style: {
        marginBottom: pageCount <= 1 ? "32px" : "0px"
      }
    }
  };

  const options = {
    selectedCategories,
    selectedRegions,
    selectedJobStatuses,
    page: currentPage,
    query,
  };

  const fetchCandidates = ({
    page = currentPage,
    selectedCategories,
    selectedRegions,
    selectedJobStatuses,
    query,
  } = {}) => {
    setLoading(true);
    setPageLoading(true);

    const params = {
      authenticity_token: authenticityToken,
      selected_categories: selectedCategories?.map(category => category.id),
      selected_regions: selectedRegions,
      selected_job_statuses: selectedJobStatuses,
      query: query,
      page
    };

    sh.get("internal/candidates", { params })
      .then((res) => {
        setCandidates(res?.data?.data?.query);
        setPageCount(res.data?.pagy?.pages);
        setCount(res?.data?.pagy?.count);
      })
      .catch((error) => {
        console.error("Error fetching candidates:", error);
      })
      .finally(() => {
        setLoading(false);
        setPageLoading(false);
      });
  };

  const debouncedFetch = debounce((value) => {
    setCurrentPage(1);
    fetchCandidates({
      ...options,
      query: value,
      page: 1
    });
  }, 300);

  const handleSearch = (event) => {
    const value = event.target.value;
    setQuery(value);
    debouncedFetch(value);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    fetchCandidates({
      ...options,
      page: value
    });
  };

  const handleCategoryChange = (selectedCategories) => {
    setCurrentPage(1);
    fetchCandidates({
      selectedRegions,
      selectedCategories: selectedCategories,
      selectedJobStatuses,
      query
    });
    setSelectedCategories(selectedCategories);
  };

  const selectAllCategories = (department) => {
    const allCategoriesInDept = organizedCategories[department];
    const newSelectedCategories = [...selectedCategories, ...allCategoriesInDept];

    const uniqueSelectedCategories = Array.from(new Set(newSelectedCategories.map(category => category.id)))
      .map(id => newSelectedCategories.find(category => category.id === id));

    handleCategoryChange(uniqueSelectedCategories);
  };

  const handleRegionChange = (selectedRegions) => {
    setCurrentPage(1);
    fetchCandidates({
      selectedCategories,
      selectedRegions: selectedRegions,
      selectedJobStatuses,
      query
    });
    setSelectedRegions(selectedRegions);
  };

  const handleJobStatusChange = (selectedJobStatuses) => {
    setCurrentPage(1);
    fetchCandidates({
      selectedCategories,
      selectedRegions,
      selectedJobStatuses: selectedJobStatuses,
      query
    });
    setSelectedJobStatuses(selectedJobStatuses);
  };

  const fetchCategories = () => {
    sh.get("internal/all_categories", {
      authenticity_token: authenticityToken,
    }).then((res) => {
      const fetchedCategories = res?.data?.categories;
      setOrganizedCategories(organizeCategoriesByDepartment(fetchedCategories));
    });
  };

  const removeAllFilters = () => {
    setCurrentPage(1);
    setSelectedCategories([]);
    setSelectedRegions([]);
    setSelectedJobStatuses([]);
    setQuery("");
    fetchCandidates();
  };

  useEffect(() => {
    fetchCandidates();
    fetchCategories();
  }, []);

  const columns = [
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
      grow: 1.5,
      width: "12%"
    },
    {
      name: "Email",
      selector: (row) => (
        <Link href={`mailto:${row.email}`} target="_blank" rel="noopener noreferrer" sx={{ cursor: "pointer" }}>
          {row.email}
        </Link>
      ),
      width: "12%"
    },
    {
      name: "Phone",
      selector: (row) => (
        <Link href={`tel:${row.phone}`} sx={{ cursor: "pointer" }}>
          {row.phone}
        </Link>
      ),
      sortable: false,
      width: "10%"
    },
    {
      name: "Applied To",
      selector: (row) => (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            lineHeight: "1.5",
            textAlign: "center",
            padding: "8px 0"
          }}
        >
          {row.search_data.applications.map((application, index) => (
            <Box key={index} sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, maxWidth: "200px", overflow: "hidden" }}>
              <Tooltip
                title={(() => {
                  const userName = application.flagged_by || "Unknown";
                  return application.flag !== "pending"
                    ? `Flagged by ${userName}`
                    : `Not flagged yet by ${userName}`;
                })()}
                arrow
              >
                <Box
                  sx={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor:
                      application.flag === "good" ? "#4caf50" :
                        application.flag === "maybe" ? "#ff9800" :
                          application.flag === "no_go" ? "#f44336" :
                            "#9e9e9e",
                    flexShrink: 0,
                    cursor: "help"
                  }}
                />
              </Tooltip>
              <Box sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
              }}>
                {application.job_title}
              </Box>
            </Box>
          ))}
        </Box>
      ),
      sortable: false,
      width: "15%"
    },
    {
      name: "Categories",
      selector: (row) => (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center", padding: "8px 0" }}>
          {row.search_data.category_ids.map((categoryId, index) => {
            const category = organizedCategories[Object.keys(organizedCategories).find(dept =>
              organizedCategories[dept].some(cat => cat.id === categoryId)
            )]?.find(cat => cat.id === categoryId);
            return category ? (
              <Chip
                key={index}
                label={category.name}
                size="small"
              />
            ) : null;
          })}
        </Box>
      ),
      sortable: false,
      width: "15%"
    },
    {
      name: "Job Status",
      selector: (row) => (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center", padding: "8px 0" }}>
          {row.search_data.job_status.map((status, index) => (
            <Chip
              key={index}
              label={capitalize(status)}
              color={statusChipColor[status] || "default"}
              size="small"
            />
          ))}
        </Box>
      ),
      sortable: false,
      width: "10%"
    },
    {
      name: "Region(s)",
      selector: (row) => (
        <Box sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          lineHeight: "1.5",
        }}>
          {row.search_data.region.map((region, index) => (
            <div key={index}>
              {formatRegion(region)}
            </div>
          ))}
        </Box>
      ),
      sortable: false,
      width: "10%"
    },
    {
      name: "Resume",
      selector: (row) => (
        <Link href={row.last_resume_url} target="_blank" rel="noopener noreferrer" sx={{ cursor: "pointer" }}>
          View Resume
        </Link>
      ),
      sortable: false,
      width: "10%"
    },
    {
      name: "",
      selector: (row) => (
        <Link
          href={`https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(row.name)}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <LinkedInIcon sx={{ color: "#0077B5", fontSize: "28px" }} />
        </Link>
      ),
      sortable: false,
      width: "6%"
    },
  ];

  return (
    <Container maxWidth="100%">
      <Box style={{ marginTop: "26px"}}>
        <h1 style={{ fontSize: "24px" }}>Candidates & Resumes</h1>
      </Box>
      <Box style={{ display: "flex", justifyContent: "space-between", marginTop: "24px" }}>
        <Box style={{ display: "flex", columnGap: "16px", marginBottom: "16px", flex: 1 }}>
          <Box sx={{ width: "240px" }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search candidates..."
              value={query}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box>
            <FormControl sx={{ width: "240px", ".MuiOutlinedInput-root": { height: "40px" } }}>
              <InputLabel id="demo-multiple-checkbox-label" sx={{ top: selectedCategories.length === 0 ? "-6px" : "0" }}>Select Categories</InputLabel>
              <Select
                labelId="demo-multiple-checkbox-label"
                id="demo-multiple-checkbox"
                multiple
                value={selectedCategories}
                onChange={(event) => handleCategoryChange(event.target.value)}
                input={<OutlinedInput label="Select Categories" />}
                renderValue={(selected) => selected.map(item => item.name).join(", ")}
              >
                {Object.entries(organizedCategories).map(([department, categories]) => [
                  <ListSubheader key={department} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    {department}
                    <Button
                      size="small"
                      onClick={() => selectAllCategories(department)}
                      sx={{ textTransform: "none" }}
                    >
                      Select All
                    </Button>
                  </ListSubheader>,
                  ...categories.map((category) => (
                    <MenuItem
                      key={category.id}
                      value={category}
                      sx={{
                        padding: "0px 8px",
                        "&.Mui-selected": {
                          backgroundColor: "transparent",
                          "&:hover": {
                            backgroundColor: "transparent",
                          },
                        },
                        "&:hover": {
                          backgroundColor: "transparent",
                        },
                      }}
                    >
                      <Checkbox checked={selectedCategories.includes(category)} />
                      <ListItemText primary={category.name} />
                    </MenuItem>
                  ))
                ])}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <FormControl sx={{ width: "240px", ".MuiOutlinedInput-root": { height: "40px" } }}>
              <InputLabel id="demo-multiple-checkbox-label" sx={{ top: selectedRegions.length === 0 ? "-6px" : "0" }}>Select Regions</InputLabel>
              <Select
                labelId="demo-multiple-checkbox-label"
                id="demo-multiple-checkbox"
                multiple
                value={selectedRegions}
                onChange={(event) => handleRegionChange(event.target.value)}
                input={<OutlinedInput label="Select Regions" />}
                renderValue={(selected) => selected.map(item => formatRegion(item)).join(", ")}
              >
                <ListSubheader>Belgium</ListSubheader>
                {belgianRegions.map((name) => (
                  <MenuItem key={name} value={name} sx={{ padding: "0px 8px" }}>
                    <Checkbox checked={selectedRegions.indexOf(name) > -1} />
                    <ListItemText primary={formatRegion(name)} />
                  </MenuItem>
                ))}
                <ListSubheader>France</ListSubheader>
                {frenchRegions.map((name) => (
                  <MenuItem key={name} value={name} sx={{ padding: "0px 8px" }}>
                    <Checkbox checked={selectedRegions.indexOf(name) > -1} />
                    <ListItemText primary={formatRegion(name)} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <FormControl sx={{ width: "240px", ".MuiOutlinedInput-root": { height: "40px" } }}>
              <InputLabel id="job-status-checkbox-label" sx={{ top: selectedJobStatuses.length === 0 ? "-6px" : "0" }}>Select Job Status</InputLabel>
              <Select
                labelId="job-status-checkbox-label"
                id="job-status-checkbox"
                multiple
                value={selectedJobStatuses}
                onChange={(event) => handleJobStatusChange(event.target.value)}
                input={<OutlinedInput label="Select Job Status" />}
                renderValue={(selected) => selected.map(item => capitalize(item)).join(", ")}
              >
                {jobStatuses.map((status) => (
                  <MenuItem key={status} value={status} sx={{ padding: "0px 8px" }}>
                    <Checkbox checked={selectedJobStatuses.indexOf(status) > -1} />
                    <ListItemText primary={capitalize(status)} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box style={{ display: "flex", alignItems: "center", marginLeft: "32px" }}>
            <Link onClick={removeAllFilters} sx={{ cursor: "pointer" }}>Clear all</Link>
          </Box>
        </Box>
      </Box>
      <Divider style={{ marginTop: "8px" }} />
      <DataTable
        columns={columns}
        data={candidates}
        defaultSortField="name"
        defaultSortAsc={false}
        pointerOnHover
        responsive
        striped
        progressPending={loading || pageLoading}
        progressComponent={
          <Box sx={{ padding: "36px" }}>
            <CircularProgress />
          </Box>
        }
        noDataComponent={
          <Box style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", rowGap: "16px", padding: "36px" }}>
            <h3>No candidates found... Try another search or filter.</h3>
          </Box>
        }
        customStyles={customStyles}
      />
      {!loading && pageCount > 1 && (
        <Box display="flex" justifyContent="center" mt={4} mb={4}>
          <Pagination count={pageCount} page={currentPage} onChange={handlePageChange} />
        </Box>
      )}
    </Container>
  );
};

export default Resumes;
